/**
 * Heart Rate Tracking Service
 * 
 * Connects to Bluetooth HR monitors using Web Bluetooth API
 * Supports standard HR monitors following the Heart Rate Service specification
 */

export interface HeartRateData {
  bpm: number;
  timestamp: number;
  zone?: number;
  rrInterval?: number; // R-R interval for HRV analysis (milliseconds)
}

export interface HeartRateZoneConfig {
  maxHR: number; // User's maximum heart rate
  zones?: {
    zone1Max: number; // % of max HR
    zone2Max: number;
    zone3Max: number;
    zone4Max: number;
    // Zone 5 is everything above zone4Max
  };
}

export class HeartRateTracker {
  private device: BluetoothDevice | null = null;
  private server: BluetoothRemoteGATTServer | null = null;
  private hrCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private listeners: ((data: HeartRateData) => void)[] = [];
  private isConnected: boolean = false;
  private currentBPM: number = 0;
  private maxHR: number;
  private zones: { zone1Max: number; zone2Max: number; zone3Max: number; zone4Max: number };
  
  // Heart Rate Service UUID (standard)
  private readonly HR_SERVICE_UUID = 0x180D;
  private readonly HR_MEASUREMENT_UUID = 0x2A37;

  constructor(config: HeartRateZoneConfig) {
    this.maxHR = config.maxHR || 200; // Default fallback
    this.zones = config.zones || {
      zone1Max: 0.6,  // 60% of max HR
      zone2Max: 0.7,  // 70% of max HR
      zone3Max: 0.8,  // 80% of max HR
      zone4Max: 0.9,  // 90% of max HR
      // Zone 5 is above 90%
    };
  }

  /**
   * Request connection to a Bluetooth HR monitor
   * Must be called from a user gesture (button click)
   */
  async connect(): Promise<void> {
    try {
      // Check if Web Bluetooth is available
      if (!navigator.bluetooth) {
        throw new Error('Web Bluetooth API not available in this browser');
      }

      // Request device
      this.device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [this.HR_SERVICE_UUID] }],
        optionalServices: [this.HR_SERVICE_UUID]
      });

      // Listen for disconnection
      this.device.addEventListener('gattserverdisconnected', this.handleDisconnect);

      // Connect to GATT server
      this.server = await this.device.gatt!.connect();
      
      // Get Heart Rate service
      const service = await this.server.getPrimaryService(this.HR_SERVICE_UUID);
      
      // Get Heart Rate Measurement characteristic
      this.hrCharacteristic = await service.getCharacteristic(this.HR_MEASUREMENT_UUID);
      
      // Start receiving notifications
      await this.hrCharacteristic.startNotifications();
      this.hrCharacteristic.addEventListener('characteristicvaluechanged', this.handleHRValue);
      
      this.isConnected = true;
      console.log('Heart rate monitor connected:', this.device.name);
    } catch (error) {
      console.error('Failed to connect to HR monitor:', error);
      throw error;
    }
  }

  /**
   * Disconnect from HR monitor
   */
  async disconnect(): Promise<void> {
    if (this.hrCharacteristic) {
      try {
        await this.hrCharacteristic.stopNotifications();
        this.hrCharacteristic.removeEventListener('characteristicvaluechanged', this.handleHRValue);
      } catch (error) {
        console.error('Error stopping notifications:', error);
      }
    }

    if (this.server) {
      this.server.disconnect();
    }

    this.device = null;
    this.server = null;
    this.hrCharacteristic = null;
    this.isConnected = false;
    console.log('Heart rate monitor disconnected');
  }

  /**
   * Handle incoming heart rate data
   */
  private handleHRValue = (event: Event): void => {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    const value = target.value;
    
    if (!value) return;

    // Parse Heart Rate Measurement data according to BLE spec
    // Byte 0: Flags
    const flags = value.getUint8(0);
    const is16Bit = flags & 0x01; // Heart Rate Value Format bit
    
    // Byte 1-2: Heart Rate Value
    let bpm: number;
    if (is16Bit) {
      bpm = value.getUint16(1, true); // little-endian
    } else {
      bpm = value.getUint8(1);
    }
    
    this.currentBPM = bpm;
    
    // Optional: Extract R-R interval if present (for HRV)
    let rrInterval: number | undefined;
    const rrPresent = flags & 0x10; // RR-Interval bit
    if (rrPresent) {
      const offset = is16Bit ? 3 : 2;
      if (value.byteLength >= offset + 2) {
        rrInterval = value.getUint16(offset, true); // in 1/1024 seconds
      }
    }

    // Calculate zone
    const zone = this.calculateZone(bpm);

    const data: HeartRateData = {
      bpm,
      timestamp: Date.now(),
      zone,
      rrInterval
    };

    // Notify all listeners
    this.listeners.forEach(fn => fn(data));
  };

  /**
   * Handle disconnection
   */
  private handleDisconnect = (): void => {
    console.log('Heart rate monitor disconnected');
    this.isConnected = false;
    this.device = null;
    this.server = null;
    this.hrCharacteristic = null;
  };

  /**
   * Register callback for heart rate updates
   */
  onHeartRateUpdate(callback: (data: HeartRateData) => void): void {
    this.listeners.push(callback);
  }

  /**
   * Remove callback
   */
  removeListener(callback: (data: HeartRateData) => void): void {
    this.listeners = this.listeners.filter(fn => fn !== callback);
  }

  /**
   * Calculate HR zone based on percentage of max HR
   */
  calculateZone(bpm: number): number {
    const percentage = bpm / this.maxHR;
    
    if (percentage < this.zones.zone1Max) return 1;
    if (percentage < this.zones.zone2Max) return 2;
    if (percentage < this.zones.zone3Max) return 3;
    if (percentage < this.zones.zone4Max) return 4;
    return 5;
  }

  /**
   * Get zone name and description
   */
  getZoneInfo(zone: number): { name: string; description: string; color: string } {
    const zones = {
      1: { name: 'Recovery', description: 'Very light, easy pace', color: '#94a3b8' },
      2: { name: 'Aerobic', description: 'Comfortable, sustainable', color: '#3b82f6' },
      3: { name: 'Tempo', description: 'Moderate, controlled effort', color: '#22c55e' },
      4: { name: 'Threshold', description: 'Hard, sustained effort', color: '#eab308' },
      5: { name: 'VO2 Max', description: 'Maximum effort', color: '#ef4444' }
    };
    
    return zones[zone as keyof typeof zones] || zones[3];
  }

  /**
   * Update max HR (for zone calculation)
   */
  setMaxHR(maxHR: number): void {
    this.maxHR = maxHR;
  }

  /**
   * Get current BPM
   */
  getCurrentBPM(): number {
    return this.currentBPM;
  }

  /**
   * Get connection status
   */
  getIsConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Get device name
   */
  getDeviceName(): string | null {
    return this.device?.name || null;
  }

  /**
   * Check if Web Bluetooth is supported
   */
  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'bluetooth' in navigator;
  }
}

